import { AlertCircle, AlertTriangle } from "lucide-react";
import type { DocumentsResponse } from "~/components/company/tabs/documents-tab";
import { ReportDataIssueLink } from "~/components/data/report-data-issue-link";
import { Skeleton } from "~/components/ui/skeleton";
import { formatDate } from "~/lib/format";
import { deriveGovernmentDependency } from "~/lib/government-dependency";
import type { CompanyTenderStats } from "~/lib/tenders";
import type { CompanyChangeEvent, CompanyProfile, RelatedCompaniesResponse } from "~/types/company";
import type { CorporateEventsResponse } from "~/types/corporate";
import { ActivitiesCard } from "./activities-card";
import { BasicInfoCard } from "./basic-info-card";
import { ChangesCard } from "./changes-card";
import { CompanyFaq } from "./company-faq";
import { EventsTeaserCard } from "./events-teaser-card";
import { FinancialCard } from "./financial-card";
import { GovernmentDependencyCard } from "./government-dependency-card";
import { PeopleCard } from "./people-card";
import { RelatedCompaniesCards } from "./related-companies";
import { RiskCard } from "./risk-card";
import {
	ContactsCard,
	ContractsTeaserCard,
	DocumentsTeaserCard,
	TendersTeaserCard,
} from "./teasers";
import { VatCard } from "./vat-card";

interface OverviewHubProps {
	company: CompanyProfile;
	overview: {
		changes: CompanyChangeEvent[];
		documents: DocumentsResponse;
		contracts: {
			total: number;
			totalValueCzk: number | null;
			supplierCount: number;
			buyerCount: number;
		};
		related: RelatedCompaniesResponse;
		events: CorporateEventsResponse;
		tenderStats: CompanyTenderStats | null;
	} | null;
	overviewStatus: "pending" | "error" | "success";
	onRetry: () => void;
}

function OverviewTeaserSkeleton() {
	return (
		<div className="card-surface rounded-xl p-6" aria-hidden="true">
			<div className="mb-4 flex items-center gap-2.5">
				<Skeleton className="h-9 w-9 rounded-xl" />
				<Skeleton className="h-5 w-36" />
			</div>
			<div className="flex flex-col gap-2.5">
				<Skeleton className="h-4 w-full" />
				<Skeleton className="h-4 w-4/5" />
				<Skeleton className="h-4 w-2/3" />
			</div>
		</div>
	);
}

function OverviewDataError({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="rounded-xl bg-error/10 p-4 lg:col-span-2" role="alert">
			<div className="flex flex-wrap items-center gap-3">
				<AlertCircle className="h-5 w-5 shrink-0 text-error" />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium">Doplňující údaje se nepodařilo načíst</p>
					<p className="text-xs text-muted-foreground">
						Základní profil je dostupný. Změny, dokumenty a smlouvy můžete načíst znovu.
					</p>
				</div>
				<button type="button" className="btn btn-outline btn-sm" onClick={onRetry}>
					Zkusit znovu
				</button>
			</div>
		</div>
	);
}

/** Přehled hub — two-column card grid per the §3 wireframe (single column on mobile). */
export function OverviewHub({ company, overview, overviewStatus, onRetry }: OverviewHubProps) {
	const changes = overview?.changes ?? null;
	const documents = overview?.documents ?? null;
	const contracts = overview?.contracts ?? null;
	const related = overview?.related ?? null;
	const events = overview?.events ?? null;
	const tenderStats = overview?.tenderStats ?? null;
	const hasDocumentsTeaser = (documents?.documents.length ?? 0) > 0;
	const hasContractsTeaser = (contracts?.total ?? 0) > 0;
	const hasTendersTeaser =
		(tenderStats?.asSupplier.participations ?? 0) + (tenderStats?.asBuyer.tenders ?? 0) > 0;
	const governmentDependency =
		deriveGovernmentDependency(company.financials, contracts) ?? company.governmentDependency;
	const firstActiveProceeding = company.insolvencyProceedings.find(
		(proceeding) => !["RESOLVED", "DISMISSED", "UNKNOWN"].includes(proceeding.status),
	);

	return (
		<div className="flex flex-col gap-4 pt-4">
			{/* Insolvency alert */}
			{company.hasActiveInsolvency && (
				<div className="rounded-xl bg-red-50 p-4 ring-1 ring-red-100">
					<div className="flex items-start gap-3">
						<AlertTriangle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
						<div className="flex flex-1 flex-col gap-1">
							<p className="text-sm font-medium text-red-800">Ověřený aktivní záznam v ISIR</p>
							{company.insolvencyProceedings
								.filter((proc) => !["RESOLVED", "DISMISSED", "UNKNOWN"].includes(proc.status))
								.map((proc) => (
									<p key={proc.id} className="text-xs text-red-700">
										Sp. zn. {proc.caseNumber}
										{proc.court && ` – ${proc.court}`}
										{proc.filedAt && ` (podáno ${formatDate(proc.filedAt)})`}
									</p>
								))}
							<ReportDataIssueLink
								subject="INSOLVENCY"
								companyIco={company.ico}
								companyName={company.name}
								targetRecordId={firstActiveProceeding?.id}
								returnTo={`/firma/${company.ico}`}
								className="btn btn-ghost btn-xs w-fit text-red-700"
							/>
						</div>
					</div>
				</div>
			)}

			<div className="grid gap-4 lg:grid-cols-2 lg:items-start">
				<BasicInfoCard company={company} />
				<PeopleCard company={company} />
				<RiskCard company={company} />
				<FinancialCard company={company} />
				{overviewStatus === "pending" && (
					<>
						<OverviewTeaserSkeleton />
						<OverviewTeaserSkeleton />
					</>
				)}
				{overviewStatus === "error" && <OverviewDataError onRetry={onRetry} />}
				{overviewStatus === "success" && (
					<>
						<ChangesCard changes={changes} />
						<EventsTeaserCard events={events} />
					</>
				)}
				<ActivitiesCard company={company} />
				<VatCard company={company} />
				{overviewStatus === "pending" && <OverviewTeaserSkeleton />}
				{overviewStatus === "success" &&
					(hasDocumentsTeaser || hasContractsTeaser || hasTendersTeaser) && (
						<div className="flex flex-col gap-4">
							<TendersTeaserCard stats={tenderStats} />
							<DocumentsTeaserCard documents={documents} />
							<ContractsTeaserCard contracts={contracts} />
						</div>
					)}
			</div>

			<ContactsCard company={company} />

			{governmentDependency && governmentDependency.contractCount > 0 && (
				<GovernmentDependencyCard dependency={governmentDependency} />
			)}

			<CompanyFaq company={company} />

			{/* ⑪ SEO blocks below the FAQ per the §3 wireframe */}
			{overviewStatus === "pending" ? (
				<div className="grid gap-4 lg:grid-cols-2">
					<OverviewTeaserSkeleton />
					<OverviewTeaserSkeleton />
				</div>
			) : (
				<RelatedCompaniesCards related={related} />
			)}
		</div>
	);
}
